import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Shield, PhoneCall } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useApi } from '../hooks/useApi';
import BrandLogo from './common/BrandLogo';
import branding from '@/config/branding';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { data: settings } = useApi('settings', api.settings.get);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 15);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Catalogue', path: '/catalogue' },
    { name: 'Carte', path: '/carte' },
    { name: 'Programmes', path: '/programmes' },
    { name: 'Investisseurs', path: '/investisseurs' },
    { name: 'Blog', path: '/blog' },
    { name: 'FAQ', path: '/faq' },
    { name: 'À Propos', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-[#080808]/90 backdrop-blur-xl border-b border-[#222222] shadow-2xl py-0' 
        : 'bg-gradient-to-b from-[#080808]/80 to-transparent py-2'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <BrandLogo 
              context="header"
              src={settings?.logo_url || branding.logo} 
              alt={`${settings?.company_name || branding.name} - ${branding.tagline}`}
              className="transform transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-7">
            {(Array.isArray(navLinks) ? navLinks : []).map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[13.5px] font-medium tracking-wide transition-all duration-200 relative py-1 ${
                    isActive 
                      ? 'text-[#C9A227] font-semibold' 
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-[#C9A227] to-[#E1C45A] transition-all duration-300 rounded-full ${
                    isActive ? 'w-full opacity-100' : 'w-0 opacity-0 group-hover:w-full'
                  }`} />
                </Link>
              );
            })}
          </div>

          {/* Actions & Portal */}
          <div className="hidden lg:flex items-center space-x-4">
            {user ? (
              <Link
                to="/admin"
                className="flex items-center gap-2 px-3.5 py-2 bg-[#111111] border border-[#C9A227]/40 text-[#C9A227] text-xs font-bold rounded-lg hover:bg-[#C9A227] hover:text-[#080808] transition-all shadow-xs"
              >
                <Shield className="w-3.5 h-3.5" />
                <span>Console Admin</span>
              </Link>
            ) : (
              <Link
                to="/admin/login"
                className="flex items-center gap-1.5 px-3 py-2 text-gray-400 hover:text-white text-xs font-semibold rounded-lg hover:bg-white/5 transition-all"
              >
                <Shield className="w-3.5 h-3.5" />
                <span>Espace Privé</span>
              </Link>
            )}

            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#C9A227] to-[#E1C45A] text-[#080808] font-bold text-xs tracking-wider uppercase rounded-lg hover:brightness-110 transition-all transform hover:scale-[1.02] shadow-md shadow-[#C9A227]/10"
            >
              <PhoneCall className="w-3.5 h-3.5" />
              <span>Investir</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-300 hover:text-white p-2.5 rounded-lg hover:bg-white/5 transition-colors border border-transparent hover:border-white/10"
            aria-label="Ouvrir le menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6 text-[#C9A227]" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-[#080808]/98 backdrop-blur-2xl border-b border-[#222222] shadow-2xl animate-fade-in">
          <div className="px-5 py-6 space-y-2">
            {(Array.isArray(navLinks) ? navLinks : []).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center py-2.5 px-4 rounded-lg text-sm transition-all ${
                  location.pathname === link.path
                    ? 'bg-[#C9A227]/15 text-[#C9A227] font-semibold border-l-2 border-[#C9A227]'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <div className="pt-4 mt-2 border-t border-white/10 space-y-3">
              {user ? (
                <Link
                  to="/admin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 py-3 px-4 bg-[#111111] text-[#C9A227] text-sm font-semibold rounded-lg border border-[#C9A227]/30"
                >
                  <Shield className="w-4 h-4" />
                  <span>Accéder au Dashboard Admin</span>
                </Link>
              ) : (
                <Link
                  to="/admin/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 py-2.5 px-4 bg-white/5 text-gray-300 text-sm font-medium rounded-lg border border-white/10"
                >
                  <Shield className="w-4 h-4" />
                  <span>Connexion Espace Privé</span>
                </Link>
              )}

              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-[#C9A227] to-[#E1C45A] text-[#080808] font-bold text-sm uppercase tracking-wider rounded-lg shadow-lg shadow-[#C9A227]/20"
              >
                <PhoneCall className="w-4 h-4" />
                <span>Nous Contacter</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
